import React, { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { AuthProvider } from './context/AuthContext';
import { ProductProvider } from './context/ProductContext';
import AppRoutes from './routes/AppRoutes';
import CookieConsent from './components/common/CookieConsent';
import useAnalytics from './hooks/useAnalytics';
import useTracking from './hooks/useTracking';
import { startClickTracker, stopClickTracker } from './services/clickTracker';
import { initI18n, getLangFromPath, resolveInitialLangRedirect } from './i18n/i18n';
import { captureRefCode } from './services/referral.service';

// Global Styles
import './styles/index.css';

/**
 * Internationalisierung (siehe i18n/i18n.js für die Konventionen):
 * - Sprache wird einmalig aus dem URL-Präfix ermittelt (/en/... → en).
 * - BrowserRouter bekommt basename="/en" für die englische Version —
 *   dadurch funktionieren ALLE bestehenden Routen, <Link>s und navigate()
 *   unverändert in beiden Sprachen, ohne den Routen-Baum zu duplizieren.
 * - Ein Sprachwechsel ist immer eine echte Navigation (Hard-Link), nie
 *   nur ein State-Wechsel — URL bleibt Single Source of Truth.
 */
const initialRedirect = resolveInitialLangRedirect();
if (initialRedirect) {
  window.location.replace(initialRedirect);
}
const lang = getLangFromPath(window.location.pathname);
initI18n(lang);
document.documentElement.lang = lang;

// Creator-Referral: ?ref=-Code beim App-Start einsammeln (30 Tage gültig).
// Eingelöst wird er nach der Registrierung in AuthContext.
captureRefCode();

/**
 * ErrorBoundary fängt unbehandelte Render-Fehler ab und zeigt eine Fallback-UI.
 * Übersetzungen hier bewusst statisch (beide Sprachen direkt), da der Fehler
 * auch im i18n-Layer selbst liegen könnte.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error('ErrorBoundary caught an error:', error, info);
  }

  render() {
    if (this.state.hasError) {
      const en = document.documentElement.lang === 'en';
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>{en ? 'Something went wrong.' : 'Etwas ist schiefgelaufen.'}</h2>
          <button onClick={() => window.location.reload()}>
            {en ? 'Reload page' : 'Seite neu laden'}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Analytics-Tracker — eigene Komponente, damit useAnalytics() innerhalb von
 * BrowserRouter + AuthProvider läuft (useLocation + useAuth).
 */
function AnalyticsTracker() {
  useAnalytics();   // GA4 (aggregiert)
  useTracking();    // eigene Pipeline (User-Journey für Mission Control)
  useEffect(() => {
    startClickTracker();
    return () => stopClickTracker();
  }, []);
  return null;
}

/**
 * Stellt sicher, dass i18n-Namespaces geladen sind, bevor die App rendert
 * (useSuspense lädt 'common' über diesen Boundary nach).
 */
function I18nReady({ children }) {
  const { ready } = useTranslation();
  if (!ready) return null;
  return children;
}

/**
 * MoneMee - Main App Component
 */
function App() {
  // Während des Sprach-Redirects nichts rendern (vermeidet Flackern)
  if (initialRedirect) {
    return null;
  }

  return (
    <HelmetProvider>
      <BrowserRouter basename={lang === 'en' ? '/en' : undefined}>
        <AuthProvider>
          <ProductProvider>
            <ErrorBoundary>
              <I18nReady>
                <AnalyticsTracker />
                <AppRoutes />
                <CookieConsent />
              </I18nReady>
            </ErrorBoundary>
          </ProductProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
